import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import resolver from "./utils/secure-resolver";

const CONNECTION_STATUS_CONNECTING = 0;
const CONNECTION_STATUS_OPEN = 1;
const CONNECTION_STATUS_CLOSING = 2;
const CONNECTION_STATUS_CLOSED = 3;

export default function useDevices({ host, query, useFirst = false }) {
  const options = useMemo(
    () => ({
      shouldReconnect: (closeEvent) => true,
      // share: true,
      reconnectAttempts: 10,
      reconnectInterval: 3000,
    }),
    []
  );

  const getSocketUrl = useCallback(
    async () =>
      fetch(host)
        .then((res) => res.json())
        .then(async (res) => {
          const isServer = res.class.includes("server");

          const link = isServer
            ? await fetch(
                new URL(
                  res.links.find((link) => link.rel.includes("self")).href
                ).origin
              )
                .then((res) => res.json())
                .then(
                  (res) =>
                    res.links.find((link) => link.rel[0].includes("events"))
                      .href
                )
            : res.links.find((link) => link.rel[0].includes("events")).href;
          return resolver(link);
        }),
    [host]
  );

  const [socketPath, setSocketPath] = useState();
  const [error, setError] = useState();
  const [devices, setDevices] = useState([]);
  const [socketUrl, setSocketUrl] = useState("");
  const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocket(
    getSocketUrl,
    options
  );

  // make initial device query
  useEffect(() => {
    if (readyState === 1) {
      const topic = `query/${query}`;
      sendMessage(JSON.stringify({ type: "subscribe", topic }));
    }
  }, [readyState, query, sendMessage]);

  useEffect(() => {
    if (lastMessage !== null) {
      const data = JSON.parse(lastMessage.data);
      const handlers = {
        error: (data) => setError(data),
        "subscribe-ack": (data) => {}, //console.log(`subscribed to: ${data.topic}`),
        event: ({ subscriptionId, topic, data }) => {
          if (subscriptionId === 1) {
            data.properties.$actions = data.actions;
            data.properties.performAction = async (action, payload) => {
              const { method, href } = data.properties.$actions.find(
                (a) => a.name === action
              );
              try {
                const { properties } = await fetch(resolver(href), {
                  method,
                  headers: {
                    "Content-type": "application/x-www-form-urlencoded",
                  },
                  body: Object.entries({ action, ...payload }).reduce(
                    (acc, [key, value]) =>
                      `${acc}${key}=${encodeURIComponent(value)}&`,
                    ""
                  ),
                }).then((res) => res.json());
                // const device = devices.find(device => device.id === properties.id);
                // device.properties = properties;
                // setDevices(devices);
              } catch (err) {
                console.error(err);
                throw err;
              }
            };

            setDevices((prev) => {
              const device = prev.find(
                (item) => item.id === data.properties.id
              );

              if (device) {
                // TODO replace instance with data.properties
                return prev;
              } else {
                return prev.concat(data.properties);
              }
            });

            // const topics = data.links
            //   .filter(link => link.rel.includes('monitor'))
            //   .map(link => {
            //     const [server, topic] = link.href.split('?');
            //     const query = topic.split('=')[1];
            //     return decodeURIComponent(query);
            //   });
            // topics.forEach(topic => {
            // });
            const root = data.links.find((link) =>
              link.rel.includes("up")
            ).href;
            const { pathname } = new URL(root);
            const server = pathname.split("/")[2];
            const topic = `${server}/${data.properties.type}/${data.properties.id}/*`;
            sendMessage(JSON.stringify({ type: "subscribe", topic }));
          } else {
            const [server, type, id, property] = topic.split("/");
            const device = devices.find((device) => device.id === id);
            device[property] = data;

            setDevices(devices);
          }
        },
      };

      const handler = handlers[data.type];

      if (!handler) {
        console.error(`No handler for ${data.type}`);
      }

      handler(data);
    }
  }, [lastMessage, setDevices, sendMessage, devices]);

  const state = {
    [ReadyState.CONNECTING]: "Connecting",
    [ReadyState.OPEN]: "Open",
    [ReadyState.CLOSING]: "Closing",
    [ReadyState.CLOSED]: "Closed",
  }[readyState];

  return [useFirst ? devices[0] || {} : devices, state, error];
}

// import { useState, useEffect } from 'haunted';

// function getError(event) {
//   return (
//     {
//       1000: 'Normal closure, meaning that the purpose for which the connection was established has been fulfilled.',
//       1001: 'An endpoint is "going away", such as a server going down or a browser having navigated away from a page.',
//       1002: 'An endpoint is terminating the connection due to a protocol error',
//       1003: 'An endpoint is terminating the connection because it has received a type of data it cannot accept (e.g., an endpoint that understands only text data MAY send this if it receives a binary message).',
//       1004: 'Reserved. The specific meaning might be defined in the future.',
//       1005: 'No status code was actually present.',
//       1006: 'The connection was closed abnormally, e.g., without sending or receiving a Close control frame',
//       1007: 'An endpoint is terminating the connection because it has received data within a message that was not consistent with the type of the message (e.g., non-UTF-8 [http://tools.ietf.org/html/rfc3629] data within a text message).',
//       1008: 'An endpoint is terminating the connection because it has received a message that "violates its policy". This reason is given either if there is no other suitable reason, or if there is a need to hide specific details about the policy.',
//       1009: 'An endpoint is terminating the connection because it has received a message that is too big for it to process',
//       1010: `Note that this status code is not used by the server, because it can fail the WebSocket handshake instead: An endpoint (client) is terminating the connection because it has expected the server to negotiate one or more extension, but the server didn't return them in the response message of the WebSocket handshake. <br /> Specifically, the extensions that are needed are: ${event.reason}`,
//       1011: 'A server is terminating the connection because it encountered an unexpected condition that prevented it from fulfilling the request.',
//       1015: "The connection was closed due to a failure to perform a TLS handshake (e.g., the server certificate can't be verified).",
//     }[event.code] || 'unknown error'
//   );
// }

// /**
//  * @typedef {Object} Device — Devices matching query
//  * @property {Object} properties - properties for device
//  * @property {Array} actions - actions for device
//  * @property {Array} links - links for device
//  */

// /**
//  *
//  * @param {string} url address for host
//  * @param {string} query query for devices
//  * @returns {[Device[], boolean, string]} yeah
//  */
// export default function useDevices(url, query) {
//   const [devices, setDevices] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(undefined);

//   useEffect(() => {
//     const encodedQuery = encodeURI(query.replace(/'/g, '"'));
//     const { host, protocol } = new URL(url);
//     const socketPrefix = protocol === 'http:' ? 'ws' : 'wss';

//     try {
//       const socket = new WebSocket(
//         `${socketPrefix}://${host}/events?topic=query%2F${encodedQuery}`,
//       );

//       socket.addEventListener('message', message => {
//         const device = JSON.parse(message.data);
//         setDevices(d => d.concat(device));
//       });

//       socket.addEventListener('open', () => setLoading(null));
//       socket.addEventListener('close', event => {
//         setError(getError(event));
//         setLoading(null);
//       });
//     } catch (err) {
//       setError(err);
//       setLoading(null);
//     }
//   }, [url, query]);

//   return [devices, loading, error];
// }

// function serializePayload(payload) {
//   return Object.keys(payload).reduce((acc, key) => {
//     if (typeof payload[key] === 'object') {
//       acc[key] = JSON.stringify(payload[key]);
//     } else {
//       acc[key] = payload[key];
//     }
//     return acc;
//   }, {});
// }

// function getDevice(device) {
//   const { actions, properties } = JSON.parse(device);

//   return {
//     properties,
//     _actionDescriptors: actions,
//     async performAction(action, payload) {
//       const deviceAction = this._actionDescriptors.find(({ name }) => name === action);

//       if (!deviceAction) {
//         throw Error(`Action '${action}' not found`);
//       }

//       const { method, href } = deviceAction;

//       const response = await fetch(href, {
//         method,
//         headers: {
//           'Content-Type': 'application/x-www-form-urlencoded',
//         },
//         body: {
//           ...action,
//           ...serializePayload(payload),
//         },
//       });
//       const data = await response.json();
//       debugger;

//       //       const XHR = this.create('iron-ajax', {
//       //           method: deviceAction.method,
//       //           url: deviceAction.href,
//       //           contentType: 'application/x-www-form-urlencoded',
//       //           body: Object.assign({
//       //               action
//       //           }, this._serializePayload(payload))
//       //       });
//       // actions
//       //       XHR.generateRequest().completes
//       //           .then(response => {
//       //               const device = response.response;
//       //               this.set('device', device);
//       //           });
//     },
//   };
// }
